/**
 * TourOverlay - Spotlight backdrop for feature tour
 *
 * Creates a semi-transparent overlay with a "hole" around the target element.
 * Uses CSS clip-path for smooth spotlight effect.
 */

import React, { useState, useEffect, useCallback } from 'react';

function TourOverlay({ targetElement }) {
  const [clipPath, setClipPath] = useState('');

  // Calculate clip path to create spotlight hole
  const updateClipPath = useCallback(() => {
    if (!targetElement) {
      // Full overlay when no target
      setClipPath('');
      return;
    }

    const rect = targetElement.getBoundingClientRect();
    const padding = 8; // Padding around element

    // Get viewport dimensions
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Calculate spotlight rectangle with padding
    const left = Math.max(0, rect.left - padding);
    const top = Math.max(0, rect.top - padding);
    const right = Math.min(vw, rect.right + padding);
    const bottom = Math.min(vh, rect.bottom + padding);

    // Create polygon that covers everything except the target area
    // Using a polygon with a hole (inset rectangle)
    const path = `polygon(
      0% 0%,
      0% 100%,
      ${left}px 100%,
      ${left}px ${top}px,
      ${right}px ${top}px,
      ${right}px ${bottom}px,
      ${left}px ${bottom}px,
      ${left}px 100%,
      100% 100%,
      100% 0%
    )`;

    setClipPath(path);
  }, [targetElement]);

  // Update on mount and when target changes
  useEffect(() => {
    updateClipPath();
  }, [updateClipPath]);

  // Update on scroll and resize
  useEffect(() => {
    const handleUpdate = () => {
      requestAnimationFrame(updateClipPath);
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [updateClipPath]);

  return (
    <div
      className="fixed inset-0 z-[9998] pointer-events-none"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        clipPath: clipPath || undefined,
        transition: 'clip-path 0.3s ease-out',
      }}
    >
      {/* Spotlight ring around target */}
      {targetElement && (
        <SpotlightRing targetElement={targetElement} />
      )}
    </div>
  );
}

/**
 * SpotlightRing - Animated ring around the target element
 */
function SpotlightRing({ targetElement }) {
  const [style, setStyle] = useState({});

  useEffect(() => {
    const updatePosition = () => {
      if (!targetElement) return;

      const rect = targetElement.getBoundingClientRect();
      const padding = 8;

      setStyle({
        position: 'fixed',
        left: rect.left - padding,
        top: rect.top - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        borderRadius: '8px',
        border: '2px solid rgba(251, 191, 36, 0.8)', // amber-400
        boxShadow: '0 0 0 4px rgba(251, 191, 36, 0.3), 0 0 20px rgba(251, 191, 36, 0.4)',
        transition: 'all 0.3s ease-out',
        pointerEvents: 'none',
      });
    };

    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [targetElement]);

  if (!targetElement) return null;

  return (
    <div
      className="animate-pulse"
      style={style}
    />
  );
}

export default TourOverlay;
